import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Warning from "@mui/icons-material/Warning";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { Dialog } from "components/Dialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  VerifiedFacilitiesForPilotQuery,
  TierIiReportKind,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

// Query to get PROCESSING facilities for a state
const VERIFIED_FACILITIES_QUERY = gql(`
  query VerifiedFacilitiesForPilot($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    chemicalFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        facilityId
        tierIIReportId
        organization
        facilityName
        streetAddress1
        city
        state
        customerFacilityId
        organizationStatus
        encampStatus
        reportKind
        partnerTenantName
        chemicalCount
        issues
        workflowStatus
      }
      count
    }
  }
`);

const ADD_FACILITY_TO_PILOT_PROGRAM_MUTATION = gql(`
  mutation AddFacilityToPilotProgram($facilityId: ID!) {
    addFacilityToPilotProgram(facilityId: $facilityId) {
      id
      type
      value
      notes
    }
  }
`);

// Query to check if tenant is denied
const DENIED_FACILITIES_CHECK_QUERY = gql(`
  query DeniedFacilitiesCheck($search: String, $page: Int, $pageSize: Int, $reportKind: TierIIReportKind) {
    deniedFacilities(search: $search, page: $page, pageSize: $pageSize, reportKind: $reportKind) {
      items {
        facility {
          id
        }
        tenant {
          id
        }
        denialReasons {
          type
        }
      }
      count
    }
  }
`);

type FacilityRow =
  VerifiedFacilitiesForPilotQuery["chemicalFacilities"]["items"][number];

interface AddPilotFacilityModalProps {
  open: boolean;
  onClose: () => void;
  onFacilityAdded: () => void;
  state: string | null;
  reportingYear: number;
  existingPilotFacilityIds: Set<string>;
}

export function AddPilotFacilityModal({
  open,
  onClose,
  onFacilityAdded,
  state,
  reportingYear,
  existingPilotFacilityIds,
}: AddPilotFacilityModalProps) {
  const alerts = useAlerts();
  const [addingFacilityId, setAddingFacilityId] = useState<string | null>(null);

  // Build default search query for PROCESSING reports in the state
  // Reports must have encampStatus of PROCESSING (being processed by Encamp)
  // Note: Using raw string values for enum filters to ensure correct query format
  const defaultSearch = useMemo(() => {
    const parts = [
      `reportingYear:${reportingYear}`,
      `encampStatus:PROCESSING`,
      `reportKind:ANNUAL`,
    ];
    if (state) {
      parts.push(`state:${state}`);
    }
    return parts.join(" ");
  }, [state, reportingYear]);

  // Check for denied tenants for facilities we're showing
  const { data: deniedData } = useQuery(DENIED_FACILITIES_CHECK_QUERY, {
    variables: {
      search: state ? `state:${state}` : "",
      page: 1,
      pageSize: 1000,
      reportKind: TierIiReportKind.Annual,
    },
    skip: !open || !state,
  });

  const deniedFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    deniedData?.deniedFacilities.items.forEach((item) => {
      // Only mark as denied if it's an explicit tenant denial
      const hasTenantDenial = item.denialReasons?.some(
        (r) => r?.type === "EXPLICIT_TENANT"
      );
      if (hasTenantDenial && item.facility?.id) {
        ids.add(item.facility.id);
      }
    });
    return ids;
  }, [deniedData]);

  const [addMutation, { loading: addLoading }] = useMutation(
    ADD_FACILITY_TO_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility added to pilot program");
        setAddingFacilityId(null);
        onFacilityAdded();
      },
      onError: (error) => {
        alerts.error(`Failed to add facility: ${error.message}`);
        setAddingFacilityId(null);
      },
    }
  );

  const handleAddFacility = useCallback(
    (facilityId: string, facilityName: string) => {
      // Prevent adding if already in progress or already a pilot
      if (addingFacilityId || existingPilotFacilityIds.has(facilityId)) {
        return;
      }

      const isDenied = deniedFacilityIds.has(facilityId);
      if (isDenied) {
        const confirmed = window.confirm(
          `Warning: The tenant for "${facilityName}" is on the DENY list. The workflow will not run until the tenant denial is removed.\n\nDo you still want to add this facility to the pilot program?`
        );
        if (!confirmed) return;
      }

      setAddingFacilityId(facilityId);
      addMutation({
        variables: {
          facilityId,
        },
      });
    },
    [
      addMutation,
      addingFacilityId,
      existingPilotFacilityIds,
      deniedFacilityIds,
      state,
      reportingYear,
    ]
  );

  const columns: OmnisearchGridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "isPilot",
        headerName: "Is Pilot",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isPilot = row.facilityId
            ? existingPilotFacilityIds.has(row.facilityId)
            : false;
          return (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              {isPilot && <Check color="success" />}
            </Stack>
          );
        },
      },
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{row.facilityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.streetAddress1, row.city, row.state]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "organization",
        headerName: "Organization",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => {
          const isDenied = row.facilityId
            ? deniedFacilityIds.has(row.facilityId)
            : false;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              sx={{ height: "100%" }}
            >
              <Typography variant="body2">{row.organization}</Typography>
              {isDenied && (
                <Tooltip title="Tenant is on the DENY list - workflow will not run">
                  <Warning color="error" fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "customerFacilityId",
        headerName: "Facility ID",
        width: 120,
      },
      {
        field: "organizationStatus",
        headerName: "Status",
        width: 120,
        renderCell: ({ row }) => (
          <Chip
            label={prettyPrintEnumValue(row.organizationStatus)}
            size="small"
            color="success"
            variant="outlined"
          />
        ),
      },
      {
        field: "encampStatus",
        headerName: "Encamp Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.encampStatus) return "-";
          return (
            <Chip
              label={prettyPrintEnumValue(row.encampStatus)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.workflowStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                Not Started
              </Typography>
            );
          }
          const color =
            row.workflowStatus === "Completed"
              ? "success"
              : row.workflowStatus === "Error"
              ? "error"
              : row.workflowStatus === "InProgress"
              ? "info"
              : "default";
          return (
            <Chip
              label={prettyPrintEnumValue(row.workflowStatus)}
              size="small"
              color={color}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const isPilot = row.facilityId
            ? existingPilotFacilityIds.has(row.facilityId)
            : false;
          if (isPilot) return null;
          const isAdding = row.facilityId === addingFacilityId;
          return (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                handleAddFacility(
                  row.facilityId ?? "",
                  row.facilityName ?? ""
                )
              }
              disabled={isAdding || addLoading || !row.facilityId}
            >
              {isAdding ? "Adding..." : "Add"}
            </Button>
          );
        },
      },
    ],
    [
      deniedFacilityIds,
      existingPilotFacilityIds,
      handleAddFacility,
      addLoading,
      addingFacilityId,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Add Pilot Facility for ${state}`}
      maxWidth="lg"
      fullWidth
      hideCancelButton
      primaryActionButton={
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Select a facility with a PROCESSING report for {reportingYear} to
            add to the pilot program. Facilities from tenants on the DENY list
            are marked with a warning.
          </Typography>

          <OmnisearchDataGrid<
            VerifiedFacilitiesForPilotQuery,
            {
              search: string;
              page: number;
              pageSize: number;
              sort: Array<{ field: string; sort: string }>;
            },
            FacilityRow
          >
            persistenceKey="add-pilot-facility-modal"
            columns={columns}
            dataQuery={VERIFIED_FACILITIES_QUERY}
            skip={!open || !state}
            defaultSearch={defaultSearch}
            getItems={(data) => data.chemicalFacilities.items}
            getCount={(data) => data.chemicalFacilities.count}
            noDataMessage={`No PROCESSING reports found for ${state} in ${reportingYear}.`}
            isURLDriven={false}
            withPadding={false}
            initialPageSize={25}
            initialSortModel={[{ field: "facilityName", sort: "asc" }]}
            showManageColumns={false}
            gridSx={{
              minHeight: "400px",
              "& .MuiDataGrid-cell": {
                py: 1,
              },
            }}
            rowHeight={56}
            disableRowSelectionOnClick
            additionalFilterColumns={[
              { header: "Facility ID", key: "customerFacilityId" },
              { header: "City", key: "city" },
              { header: "State", key: "state" },
              { header: "Organization", key: "organization" },
            ]}
          />
        </Stack>
      </Box>
    </Dialog>
  );
}
